// bluaIQ — P3: Daily Log Module
// Daily construction log with weather, crew, hours, work, issues, safety notes

import { apiFetch } from '../utils/api'
import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ClipboardList, ChevronLeft, ChevronRight, Sun, Cloud, CloudRain, Wind,
  Save, Plus, Pencil, Camera, AlertTriangle, HardHat, Truck, Users, Clock,
  Download, X, Image as ImageIcon, Check
} from 'lucide-react'
import { localizedLogField } from './dailyLogLang'
import { isDemoProject } from '../utils/demoData'

// ── TYPES ─────────────────────────────────────────

type WeatherType = 'sunny' | 'cloudy' | 'rainy' | 'windy'

// A Progress Photo as returned by the daily-log photo-linking API. The server
// sends a display `url` (served by the owner-scoped /api/uploads/* route) — it
// never sends storagePath, uploadedBy, or any private payload. `id` is used
// only for select/unlink wiring and React keys; it is never shown to the user.
interface LinkedPhoto {
  id: string
  description: string | null
  phase: string | null
  takenAt: string | null
  createdAt: string
  url: string
  alreadyLinked?: boolean
}

// Bilingual copy for the photo-linking UI (Daily Log selects EXISTING evidence
// from Progress Photos — it is not another uploader). Kept as a const so the
// exact required strings live in one place.
const PHOTO_LINK_COPY = {
  addPhotos:          { en: 'Add photos',                                  es: 'Agregar fotos' },
  selectFromProgress: { en: 'Select from Progress Photos',                es: 'Seleccionar de Fotos de Avance' },
  sameDay:            { en: 'Photos from the same day',                    es: 'Fotos del mismo día' },
  allProject:         { en: 'All project photos',                         es: 'Todas las fotos del proyecto' },
  linkedSection:      { en: 'Photos linked to this daily log',            es: 'Fotos vinculadas a esta bitácora' },
  removeAction:       { en: 'Remove from daily log',                      es: 'Quitar de la bitácora' },
  remainsAvailable:   { en: 'This photo will remain available in Progress Photos.', es: 'Esta foto seguirá disponible en Fotos de Avance.' },
  addSelected:        { en: 'Add selected',                               es: 'Agregar seleccionadas' },
  emptyLinked:        { en: 'No photos linked to this daily log yet.',    es: 'No hay fotos vinculadas a esta bitácora todavía.' },
  noSameDay:          { en: 'No photos from this day. Try all project photos.', es: 'No hay fotos del mismo día. Prueba con todas las fotos del proyecto.' },
  noProjectPhotos:    { en: 'No photos in this project. Upload photos in Progress Photos first.', es: 'No hay fotos en el proyecto. Sube fotos en Fotos de Avance primero.' },
  selectPhotos:       { en: 'Select photos', es: 'Seleccionar fotos' },
  cancel:             { en: 'Cancel', es: 'Cancelar' },
  saveOnlyNotice:     { en: 'Photos are selected from Progress Photos. No new files are uploaded here.', es: 'Las fotos se seleccionan de Fotos de Avance. No se suben archivos nuevos aquí.' },
  saveFirst:          { en: 'Save the daily log to add photos', es: 'Guarda la bitácora para agregar fotos' },
} as const

interface DailyLog {
  id: string
  date: string
  weather: WeatherType
  temperature: number
  crewCount: number
  hoursWorked: number
  workPerformed: string
  workPerformedEs: string
  issues: string
  issuesEs: string
  safetyNotes: string
  safetyNotesEs: string
  materialDeliveries: string
  materialDeliveriesEs: string
  photoCount: number
}

// ── DATE HELPERS ──────────────────────────────────

const today = new Date()
const parseLocalDate = (value: string) => {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, (month || 1) - 1, day || 1)
}
const fmt = (d: Date) => {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
const addDays = (d: Date, n: number) => { const r = new Date(d); r.setDate(r.getDate() + n); return r }

const WEATHER_CONFIG: Record<WeatherType, { icon: typeof Sun; label: string; labelEs: string; color: string }> = {
  sunny:  { icon: Sun,       label: 'Sunny',  labelEs: 'Soleado',  color: '#F59E0B' },
  cloudy: { icon: Cloud,     label: 'Cloudy', labelEs: 'Nublado',  color: '#9E9890' },
  rainy:  { icon: CloudRain, label: 'Rainy',  labelEs: 'Lluvioso', color: '#3B82F6' },
  windy:  { icon: Wind,      label: 'Windy',  labelEs: 'Ventoso',  color: '#6366F1' },
}

// ── COMPONENT ─────────────────────────────────────

export default function DailyLogModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const isEs = i18n.language === 'es'

  const [logs, setLogs] = useState<DailyLog[]>([])
  const [selectedDate, setSelectedDate] = useState(fmt(today))
  const [isEditing, setIsEditing] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  // When false, the main detail panel is hidden and the user is in a neutral
  // list-only browsing state (the All Logs list is the focus). Opening a day
  // (date nav), opening a log from the list, or creating/editing sets it true;
  // "Close and return to list" sets it false so the copy matches the behavior.
  const [detailOpen, setDetailOpen] = useState(true)

  const { data: apiLogs } = useQuery<DailyLog[]>({
    queryKey: ['daily-logs', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/daily-logs`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })

  const queryClient = useQueryClient()

  const saveMutation = useMutation({
    mutationFn: async (payload: Partial<DailyLog>) => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/daily-logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const errorBody = await res.json().catch(() => ({}))
        throw new Error(errorBody?.error || `HTTP ${res.status}`)
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-logs', projectId] })
      setIsEditing(false)
    },
    onError: (err: Error) => {
      console.error('[daily-log save] error', err)
      alert(isEs
        ? `Error al guardar el registro: ${err.message}`
        : `Failed to save log: ${err.message}`)
    },
  })

  // Merge API logs with locally-saved logs so new entries are visible
  // immediately (before the next query refetch).
  const apiList = Array.isArray(apiLogs) ? apiLogs : []
  const mergedLogs = [...apiList]
  for (const local of logs) {
    if (!mergedLogs.find(a => a.date === local.date)) mergedLogs.push(local)
  }
  mergedLogs.sort((a, b) => a.date.localeCompare(b.date))
  const activeLogs = mergedLogs.length > 0 ? mergedLogs : logs
  const currentLog = activeLogs.find(l => l.date === selectedDate)

  // ── Progress Photos linking (select existing evidence only) ──────────────
  const isDemo = isDemoProject(projectId)
  // Only persisted (non-optimistic) logs can own photo links. Optimistic rows
  // created before the first save have a synthetic id and no DB row yet.
  const realLogId = currentLog && !currentLog.id.startsWith('optimistic-') ? currentLog.id : null
  const photoLinkingEnabled = !!realLogId && !isDemo

  const [showPhotoSelector, setShowPhotoSelector] = useState(false)
  const [selectorFilter, setSelectorFilter] = useState<'same_day' | 'all'>('same_day')
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<string>>(new Set())
  const [photoToken, setPhotoToken] = useState<string | null>(null)
  const [photoLinkError, setPhotoLinkError] = useState<string | null>(null)

  // Token used to authorize <img src> against the owner-scoped /api/uploads/*
  // route (image tags can't send an Authorization header, so the token rides
  // as a query param — the same pattern the Photos module uses).
  useEffect(() => {
    let alive = true
    getToken().then(tk => { if (alive) setPhotoToken(tk || null) }).catch(() => { if (alive) setPhotoToken(null) })
    return () => { alive = false }
  }, [getToken])

  const photoSrc = (url: string) => photoToken ? `${url}?token=${encodeURIComponent(photoToken)}` : url

  const { data: photoData } = useQuery<{ candidates: LinkedPhoto[]; linked: LinkedPhoto[] }>({
    queryKey: ['daily-log-photos', projectId, realLogId, selectorFilter],
    enabled: photoLinkingEnabled,
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/daily-logs/${realLogId}/photo-candidates?filter=${selectorFilter}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('Could not load photos')
      return res.json()
    },
    retry: false,
  })
  const linkedPhotos = photoData?.linked ?? []
  const candidatePhotos = photoData?.candidates ?? []
  // When the photo-linking query has returned data, the live linked count is
  // authoritative. Fall back to the legacy `photoCount` DB field only when
  // photo data hasn't loaded yet (loading state, demo project, or unsaved log).
  const displayPhotoCount = photoData ? linkedPhotos.length : (currentLog?.photoCount ?? 0)

  const refreshPhotos = () => queryClient.invalidateQueries({ queryKey: ['daily-log-photos', projectId, realLogId] })

  const linkPhotosMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/daily-logs/${realLogId}/photos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ progressPhotoIds: ids }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || (isEs ? 'No se pudieron vincular las fotos' : 'Could not link photos'))
      }
      return res.json()
    },
    onSuccess: () => {
      refreshPhotos()
      setShowPhotoSelector(false)
      setSelectedPhotoIds(new Set())
      setPhotoLinkError(null)
    },
    onError: (err: Error) => setPhotoLinkError(err.message),
  })

  const unlinkPhotoMutation = useMutation({
    mutationFn: async (photoId: string) => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/daily-logs/${realLogId}/photos/${photoId}`, {
        method: 'DELETE',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || (isEs ? 'No se pudo quitar la foto' : 'Could not remove photo'))
      }
      return res.json()
    },
    onSuccess: () => { refreshPhotos(); setPhotoLinkError(null) },
    onError: (err: Error) => setPhotoLinkError(err.message),
  })

  const togglePhotoSelected = (id: string) => {
    setSelectedPhotoIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const openPhotoSelector = () => {
    setSelectorFilter('same_day')
    setSelectedPhotoIds(new Set())
    setPhotoLinkError(null)
    setShowPhotoSelector(true)
  }

  const navigateDay = (direction: -1 | 1) => {
    const d = parseLocalDate(selectedDate)
    d.setDate(d.getDate() + direction)
    setSelectedDate(fmt(d))
    setDetailOpen(true) // navigating to a day opens its detail
  }

  const formatDate = (d: string) => parseLocalDate(d).toLocaleDateString(isEs ? 'es-MX' : 'en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  })

  const formatDateShort = (d: string) => parseLocalDate(d).toLocaleDateString(isEs ? 'es-MX' : 'en-US', {
    month: 'short', day: 'numeric'
  })

  // ── Editable state for new/edit log ──────────
  const [editLog, setEditLog] = useState<Partial<DailyLog>>({})

  // ── PDF export state ────────────────────────
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportFrom, setExportFrom] = useState('')
  const [exportTo, setExportTo] = useState('')
  const [preview, setPreview] = useState<any>(null)
  const [exportMode, setExportMode] = useState<'optimized' | 'full'>('optimized')
  const [exportSplit, setExportSplit] = useState<'none' | 'weekly' | 'monthly'>('none')
  const [exporting, setExporting] = useState(false)

  const fetchPreview = async () => {
    if (!exportFrom || !exportTo || !projectId) { setPreview(null); return }
    try {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/daily-logs/export-preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ from: exportFrom, to: exportTo }),
      })
      if (!res.ok) { setPreview(null); return }
      const data = await res.json()
      setPreview(data)
      if (data.tier === 3 && exportSplit === 'none') setExportSplit('weekly')
    } catch {
      setPreview(null)
    }
  }

  useEffect(() => { fetchPreview() }, [exportFrom, exportTo, projectId])

  const handleExport = async () => {
    if (!projectId || !exportFrom || !exportTo) return
    setExporting(true)
    try {
      const token = await getToken()
      const params = new URLSearchParams({
        from: exportFrom,
        to: exportTo,
        mode: exportMode,
        split: exportSplit,
        lang: isEs ? 'es' : 'en',
      })
      const res = await apiFetch(`/api/projects/${projectId}/daily-logs/export?${params}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        if (err.error === 'split_required') {
          alert(t('dailyLog.export.splitRequired'))
          setExportSplit('weekly')
          return
        }
        if (err.error === 'export_limit_reached') {
          alert(t('dailyLog.export.limitReached', { plan: err.plan }))
          return
        }
        if (err.error === 'mode_not_allowed_for_plan') {
          alert(t('dailyLog.export.modeNotAllowed'))
          return
        }
        throw new Error('export failed')
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = exportSplit === 'none'
        ? `bitacora_${exportFrom}_${exportTo}.pdf`
        : `bitacora_${exportFrom}_${exportTo}_${exportSplit}.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setShowExportModal(false)
    } catch (err) {
      console.error('[daily-log export]', err)
      alert(t('dailyLog.export.failed'))
    } finally {
      setExporting(false)
    }
  }

  const startEdit = () => {
    // Always seed from currentLog if it exists — prevents empty-form data loss
    const existing = currentLog || logs.find(l => l.date === selectedDate)
    if (existing) {
      setEditLog({ ...existing })
    } else {
      setEditLog({
        date: selectedDate, weather: 'sunny', temperature: 75, crewCount: 0, hoursWorked: 0,
        workPerformed: '', workPerformedEs: '', issues: '', issuesEs: '',
        safetyNotes: '', safetyNotesEs: '', materialDeliveries: '', materialDeliveriesEs: '', photoCount: 0,
      })
    }
    setDetailOpen(true) // editing works on a specific day; keep its detail in context
    setIsEditing(true)
  }

  const saveLog = () => {
    // Build payload matching backend Zod (DailyLogSchema in src/server/index.ts:357)
    // — all fields optional except `date`. NO `id` field — backend assigns cuid.
    const payload = {
      date: selectedDate,
      weather: editLog.weather || 'sunny',
      temperature: editLog.temperature ?? 75,
      crewCount: editLog.crewCount ?? 0,
      hoursWorked: editLog.hoursWorked ?? 0,
      workPerformed: editLog.workPerformed || '',
      workPerformedEs: editLog.workPerformedEs || '',
      issues: editLog.issues || '',
      issuesEs: editLog.issuesEs || '',
      safetyNotes: editLog.safetyNotes || '',
      safetyNotesEs: editLog.safetyNotesEs || '',
      materialDeliveries: editLog.materialDeliveries || '',
      materialDeliveriesEs: editLog.materialDeliveriesEs || '',
      photoCount: editLog.photoCount ?? 0,
    }

    // Optimistic local insert so the new card appears immediately —
    // gets replaced by the real row when invalidateQueries refetches.
    const optimistic: DailyLog = {
      id: currentLog?.id || `optimistic-${Date.now()}`,
      ...payload,
    } as DailyLog
    setLogs(prev => {
      const idx = prev.findIndex(l => l.date === selectedDate)
      if (idx >= 0) return prev.map((l, i) => i === idx ? optimistic : l)
      return [...prev, optimistic].sort((a, b) => a.date.localeCompare(b.date))
    })

    // Persist to backend. onSuccess closes the editor + invalidates the query.
    saveMutation.mutate(payload)
  }

  // ── KPIs (week summary) ──────────────────────
  const weekStart = addDays(today, -6)
  const weekLogs = activeLogs.filter(l => l.date >= fmt(weekStart) && l.date <= fmt(today))
  const totalCrewHours = weekLogs.reduce((s, l) => s + l.crewCount * l.hoursWorked, 0)
  const totalPhotos = weekLogs.reduce((s, l) => s + l.photoCount, 0)
  const logsThisWeek = weekLogs.length
  const issueCount = weekLogs.filter(l => (l.issues?.length ?? 0) > 0).length

  return (
    <div className="p-5 max-w-5xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17] flex items-center gap-2">
            <ClipboardList size={18} />
            {isEs ? 'Bitácora Diaria' : 'Daily Log'}
          </h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {activeLogs.length} {isEs ? 'registros' : 'entries'} · {isEs ? 'Semana' : 'Week'}: {totalCrewHours} {isEs ? 'horas-persona' : 'crew-hours'}
          </p>
        </div>
        {!isEditing && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => {
                // Default range: last 7 days
                const end = fmt(today)
                const start = fmt(addDays(today, -6))
                setExportFrom(start)
                setExportTo(end)
                setShowExportModal(true)
              }}
              className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#06539B] text-[12px] font-[800] px-3.5 py-2 rounded-full hover:bg-[#F2F0EB]"
            >
              <Download size={14} /> {t('dailyLog.export.button')}
            </button>
            {/* Create vs edit are visually distinct: a "+" New Log for days with
                no entry, a pencil Edit Log for days that already have one — so
                the create action never reads as "+ Edit". */}
            <button onClick={startEdit} className="flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full">
              {currentLog
                ? <><Pencil size={14} /> {isEs ? 'Editar registro' : 'Edit Log'}</>
                : <><Plus size={14} /> {isEs ? 'Nueva bitácora' : 'New Log'}</>}
            </button>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{isEs ? 'Registros (7d)' : 'Logs (7d)'}</div>
          <div className="text-[18px] font-[900] text-[#06539B]">{logsThisWeek}</div>
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{isEs ? 'Horas-Persona' : 'Crew-Hours'}</div>
          <div className="text-[18px] font-[900] text-[#1C1A17]">{totalCrewHours}</div>
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{isEs ? 'Fotos (7d)' : 'Photos (7d)'}</div>
          <div className="text-[18px] font-[900] text-[#22C55E]">{totalPhotos}</div>
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{isEs ? 'Problemas (7d)' : 'Issues (7d)'}</div>
          <div className="text-[18px] font-[900] text-[#EF4444]">{issueCount}</div>
        </div>
      </div>

      {/* Date navigation */}
      <div className="bg-white border border-[#ECEAE4] rounded-xl px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigateDay(-1)} aria-label={isEs ? 'Día anterior' : 'Previous day'} title={isEs ? 'Día anterior' : 'Previous day'} className="p-1.5 rounded-lg hover:bg-[#F2F0EB] text-[#9E9890] hover:text-[#1C1A17]">
          <ChevronLeft size={18} />
        </button>
        <div className="text-center">
          <div className="text-[13px] font-[900] text-[#1C1A17] capitalize">{formatDate(selectedDate)}</div>
          <div className="text-[9px] font-[700] text-[#9E9890] mt-0.5">
            {currentLog ? (isEs ? 'Registro existente' : 'Log exists') : (isEs ? 'Sin registro' : 'No entry')}
          </div>
        </div>
        <button onClick={() => navigateDay(1)} aria-label={isEs ? 'Día siguiente' : 'Next day'} title={isEs ? 'Día siguiente' : 'Next day'} className="p-1.5 rounded-lg hover:bg-[#F2F0EB] text-[#9E9890] hover:text-[#1C1A17]">
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Log content or edit form */}
      {isEditing ? (
        /* ── EDIT FORM ──────────────────────────── */
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-[13px] font-[800] text-[#1C1A17]">
              {currentLog ? (isEs ? 'Editar Registro' : 'Edit Log') : (isEs ? 'Nuevo Registro' : 'New Log')}
            </h3>
            <div className="flex gap-2">
              <button onClick={() => setIsEditing(false)} className="text-[12px] font-[700] text-[#9E9890] px-3 py-1.5 rounded-lg border border-[#ECEAE4] hover:bg-[#F2F0EB]">
                {isEs ? 'Cancelar' : 'Cancel'}
              </button>
              <button onClick={saveLog} disabled={saveMutation.isPending} className="flex items-center gap-1 text-[10px] font-[800] text-white bg-[#06539B] px-4 py-1.5 rounded-lg">
                <Save size={12} /> {saveMutation.isPending
                  ? (isEs ? 'Guardando…' : 'Saving…')
                  : (isEs ? 'Guardar' : 'Save')}
              </button>
            </div>
          </div>

          {/* Weather & temperature */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">{isEs ? 'Clima' : 'Weather'}</label>
              <div className="flex gap-1">
                {(Object.entries(WEATHER_CONFIG) as [WeatherType, typeof WEATHER_CONFIG['sunny']][]).map(([key, cfg]) => (
                  <button
                    key={key}
                    onClick={() => setEditLog(prev => ({ ...prev, weather: key }))}
                    aria-label={isEs ? cfg.labelEs : cfg.label}
                    title={isEs ? cfg.labelEs : cfg.label}
                    aria-pressed={editLog.weather === key}
                    className={`flex-1 flex flex-col items-center gap-0.5 p-2 rounded-lg text-[8px] font-[700] transition-colors ${
                      editLog.weather === key ? 'bg-[#06539B] text-white' : 'bg-[#F2F0EB] text-[#9E9890] hover:bg-[#ECEAE4]'
                    }`}
                  >
                    <cfg.icon size={14} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">{isEs ? 'Temperatura (°F)' : 'Temperature (°F)'}</label>
              <input
                type="number" value={editLog.temperature || ''} onChange={e => setEditLog(prev => ({ ...prev, temperature: +e.target.value }))}
                className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[700] text-[#1C1A17]"
              />
            </div>
            <div>
              <label className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">{isEs ? 'Personas en obra' : 'Crew Count'}</label>
              <input
                type="number" value={editLog.crewCount || ''} onChange={e => setEditLog(prev => ({ ...prev, crewCount: +e.target.value }))}
                className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[700] text-[#1C1A17]"
              />
            </div>
            <div>
              <label className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">{isEs ? 'Horas trabajadas' : 'Hours Worked'}</label>
              <input
                type="number" value={editLog.hoursWorked || ''} onChange={e => setEditLog(prev => ({ ...prev, hoursWorked: +e.target.value }))}
                className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[700] text-[#1C1A17]"
              />
            </div>
          </div>

          {/* Text fields */}
          <div>
            <label className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">{isEs ? 'Trabajo realizado' : 'Work Performed'}</label>
            <textarea
              value={isEs ? (editLog.workPerformedEs || '') : (editLog.workPerformed || '')}
              onChange={e => setEditLog(prev => isEs ? { ...prev, workPerformedEs: e.target.value } : { ...prev, workPerformed: e.target.value })}
              rows={3} className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[11px] font-[600] text-[#1C1A17] resize-none"
            />
          </div>
          <div>
            <label className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">{isEs ? 'Problemas' : 'Issues'}</label>
            <textarea
              value={isEs ? (editLog.issuesEs || '') : (editLog.issues || '')}
              onChange={e => setEditLog(prev => isEs ? { ...prev, issuesEs: e.target.value } : { ...prev, issues: e.target.value })}
              rows={2} className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[11px] font-[600] text-[#1C1A17] resize-none"
            />
          </div>
          <div>
            <label className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">{isEs ? 'Notas de seguridad' : 'Safety Notes'}</label>
            <textarea
              value={isEs ? (editLog.safetyNotesEs || '') : (editLog.safetyNotes || '')}
              onChange={e => setEditLog(prev => isEs ? { ...prev, safetyNotesEs: e.target.value } : { ...prev, safetyNotes: e.target.value })}
              rows={2} className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[11px] font-[600] text-[#1C1A17] resize-none"
            />
          </div>
          <div>
            <label className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] block mb-1">{isEs ? 'Entregas de material' : 'Material Deliveries'}</label>
            <textarea
              value={isEs ? (editLog.materialDeliveriesEs || '') : (editLog.materialDeliveries || '')}
              onChange={e => setEditLog(prev => isEs ? { ...prev, materialDeliveriesEs: e.target.value } : { ...prev, materialDeliveries: e.target.value })}
              rows={2} className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[11px] font-[600] text-[#1C1A17] resize-none"
            />
          </div>
        </div>
      ) : !detailOpen ? (
        /* ── LIST-ONLY / NEUTRAL BROWSING ──────────
           No day is "open": the All Logs list below is the focus. Reached after
           "Close and return to list" so the copy matches the behavior. */
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-6 text-center">
          <ClipboardList size={28} className="mx-auto text-[#CECAC4] mb-2" />
          <div className="text-[12px] font-[800] text-[#1C1A17]">
            {isEs ? 'Explorando todos los registros' : 'Browsing all logs'}
          </div>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-1">
            {isEs ? 'Selecciona un registro de la lista para ver el detalle.' : 'Select a log from the list below to view its details.'}
          </p>
        </div>
      ) : currentLog ? (
        /* ── VIEW MODE ─────────────────────────── */
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-4">
          {/* Weather & stats bar */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="flex items-center gap-2 bg-[#FAFAF8] rounded-lg p-3">
              {(() => { const cfg = WEATHER_CONFIG[currentLog.weather] || WEATHER_CONFIG.sunny; const Icon = cfg.icon; return <Icon size={18} style={{ color: cfg.color }} /> })()}
              <div>
                <div className="text-[10px] font-[700] text-[#1C1A17]">
                  {isEs ? (WEATHER_CONFIG[currentLog.weather] || WEATHER_CONFIG.sunny).labelEs : (WEATHER_CONFIG[currentLog.weather] || WEATHER_CONFIG.sunny).label}
                </div>
                <div className="text-[9px] font-[600] text-[#9E9890]">{currentLog.temperature}°F</div>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-[#FAFAF8] rounded-lg p-3">
              <Users size={16} className="text-[#06539B]" />
              <div>
                <div className="text-[10px] font-[700] text-[#1C1A17]">{currentLog.crewCount} {isEs ? 'personas' : 'crew'}</div>
                <div className="text-[9px] font-[600] text-[#9E9890]">{isEs ? 'En obra' : 'On site'}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-[#FAFAF8] rounded-lg p-3">
              <Clock size={16} className="text-[#22C55E]" />
              <div>
                <div className="text-[10px] font-[700] text-[#1C1A17]">{currentLog.hoursWorked} {isEs ? 'horas' : 'hours'}</div>
                <div className="text-[9px] font-[600] text-[#9E9890]">{isEs ? 'Trabajadas' : 'Worked'}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-[#FAFAF8] rounded-lg p-3">
              <Camera size={16} className="text-[#F59E0B]" />
              <div>
                <div className="text-[10px] font-[700] text-[#1C1A17]" data-testid="daily-log-photo-count">{displayPhotoCount} {isEs ? 'fotos' : 'photos'}</div>
                <div className="text-[9px] font-[600] text-[#9E9890]">{isEs ? 'Adjuntas' : 'Attached'}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-[#FAFAF8] rounded-lg p-3">
              <HardHat size={16} className="text-[#9E9890]" />
              <div>
                <div className="text-[10px] font-[700] text-[#1C1A17]">{currentLog.crewCount * currentLog.hoursWorked} {isEs ? 'h-p' : 'c-h'}</div>
                <div className="text-[9px] font-[600] text-[#9E9890]">{isEs ? 'Horas-persona' : 'Crew-hours'}</div>
              </div>
            </div>
          </div>

          {/* Work performed */}
          <div>
            <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1.5 flex items-center gap-1">
              <ClipboardList size={11} /> {isEs ? 'Trabajo realizado' : 'Work Performed'}
            </div>
            <p className="text-[11px] font-[600] text-[#1C1A17] leading-relaxed">
              {localizedLogField(currentLog.workPerformed, currentLog.workPerformedEs, isEs)}
            </p>
          </div>

          {/* Issues */}
          {(currentLog.issues || currentLog.issuesEs) && (
            <div>
              <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#EF4444] mb-1.5 flex items-center gap-1">
                <AlertTriangle size={11} /> {isEs ? 'Problemas' : 'Issues'}
              </div>
              <p className="text-[11px] font-[600] text-[#1C1A17] leading-relaxed bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">
                {localizedLogField(currentLog.issues, currentLog.issuesEs, isEs)}
              </p>
            </div>
          )}

          {/* Safety notes */}
          {(currentLog.safetyNotes || currentLog.safetyNotesEs) && (
            <div>
              <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#F59E0B] mb-1.5 flex items-center gap-1">
                <HardHat size={11} /> {isEs ? 'Notas de seguridad' : 'Safety Notes'}
              </div>
              <p className="text-[11px] font-[600] text-[#1C1A17] leading-relaxed bg-[#FFFBEB] border border-[#FDE68A] rounded-lg px-3 py-2">
                {localizedLogField(currentLog.safetyNotes, currentLog.safetyNotesEs, isEs)}
              </p>
            </div>
          )}

          {/* Material deliveries */}
          {(currentLog.materialDeliveries || currentLog.materialDeliveriesEs) && (
            <div>
              <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1.5 flex items-center gap-1">
                <Truck size={11} /> {isEs ? 'Entregas de material' : 'Material Deliveries'}
              </div>
              <p className="text-[11px] font-[600] text-[#1C1A17] leading-relaxed">
                {localizedLogField(currentLog.materialDeliveries, currentLog.materialDeliveriesEs, isEs)}
              </p>
            </div>
          )}

          {/* ── Linked Progress Photos (select existing evidence) ──────────
              Daily Log SELECTS photos that already exist in Progress Photos —
              it is not another uploader. Removing a photo here only unlinks it;
              the original stays in Progress Photos. Hidden for demo / unsaved
              logs (no DB row to attach to yet). */}
          {photoLinkingEnabled && (
            <div data-testid="daily-log-linked-photos" className="border-t border-[#ECEAE4] pt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] flex items-center gap-1">
                  <ImageIcon size={11} /> {isEs ? PHOTO_LINK_COPY.linkedSection.es : PHOTO_LINK_COPY.linkedSection.en}
                </div>
                <button
                  type="button"
                  onClick={openPhotoSelector}
                  data-testid="daily-log-add-photos-btn"
                  className="flex items-center gap-1.5 bg-[#06539B] text-white px-3 py-1.5 rounded-full text-[10px] font-[800] hover:bg-[#14457E]"
                >
                  <Plus size={12} /> {isEs ? PHOTO_LINK_COPY.addPhotos.es : PHOTO_LINK_COPY.addPhotos.en}
                </button>
              </div>

              {photoLinkError && (
                <div className="text-[10px] font-[700] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-2">
                  {photoLinkError}
                </div>
              )}

              {linkedPhotos.length === 0 ? (
                <p className="text-[11px] font-[600] text-[#9E9890] italic">
                  {isEs ? PHOTO_LINK_COPY.emptyLinked.es : PHOTO_LINK_COPY.emptyLinked.en}
                </p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {linkedPhotos.map(photo => (
                    <div key={photo.id} className="relative rounded-lg overflow-hidden bg-[#F2F0EB] aspect-square">
                      <img
                        src={photoSrc(photo.url)}
                        alt={photo.description || (isEs ? 'Foto vinculada' : 'Linked photo')}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {/* Unlink affordance is ALWAYS visible (no hover-only) so it
                          works on touch devices where there is no hover. Removing
                          only unlinks — the original photo stays in Progress Photos. */}
                      <button
                        type="button"
                        onClick={() => unlinkPhotoMutation.mutate(photo.id)}
                        disabled={unlinkPhotoMutation.isPending}
                        title={isEs ? PHOTO_LINK_COPY.removeAction.es : PHOTO_LINK_COPY.removeAction.en}
                        aria-label={isEs ? PHOTO_LINK_COPY.removeAction.es : PHOTO_LINK_COPY.removeAction.en}
                        data-testid="daily-log-unlink-photo-btn"
                        className="absolute top-1 right-1 p-1.5 rounded-full bg-black/60 hover:bg-red-500 text-white transition-colors disabled:opacity-50"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[9px] font-[600] text-[#9E9890] mt-1.5">
                {isEs ? PHOTO_LINK_COPY.remainsAvailable.es : PHOTO_LINK_COPY.remainsAvailable.en}
              </p>
            </div>
          )}

          {/* Unsaved (optimistic) log: photos attach to a persisted log, so prompt
              the user to save first. Hidden for demo projects. Low-risk hint only. */}
          {currentLog && !isDemo && !realLogId && (
            <div data-testid="daily-log-photos-save-first" className="border-t border-[#ECEAE4] pt-3 text-[10px] font-[700] text-[#9E9890] flex items-center gap-1.5">
              <ImageIcon size={11} /> {isEs ? PHOTO_LINK_COPY.saveFirst.es : PHOTO_LINK_COPY.saveFirst.en}
            </div>
          )}
        </div>
      ) : (
        /* ── EMPTY STATE ───────────────────────── */
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-8 text-center">
          <ClipboardList size={32} className="mx-auto text-[#CECAC4] mb-3" />
          <div className="text-[13px] font-[800] text-[#1C1A17]">
            {isEs ? 'Sin registro para este día' : 'No log for this date'}
          </div>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-1">
            {isEs ? 'Haz clic en "Nueva bitácora" para agregar uno' : 'Click "New Log" to add one'}
          </p>
        </div>
      )}

      {/* Recent logs timeline */}
      <div className="bg-white border border-[#ECEAE4] rounded-xl p-4">
        <h3 className="text-[11px] font-[800] text-[#1C1A17] mb-3">
          {isEs ? 'Todos los registros' : 'All Logs'}
        </h3>
        <div className="space-y-2">
          {activeLogs.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardList size={36} className="mx-auto text-[#ECEAE4]" />
              <p className="text-[12px] font-[700] text-[#9E9890] mt-3">
                {isEs ? 'No hay registros todavía' : 'No daily logs yet'}
              </p>
              <p className="text-[11px] font-[600] text-[#9E9890] mt-1">
                {isEs ? 'Crea tu primer registro con el botón "Nueva bitácora"' : 'Create your first log with the "New Log" button'}
              </p>
            </div>
          ) : [...activeLogs].reverse().map(log => {
            const wCfg = WEATHER_CONFIG[log.weather] || WEATHER_CONFIG.sunny
            const WIcon = wCfg.icon
            const isSelected = log.date === selectedDate
            const isExpanded = expandedId === log.id
            return (
              <div key={log.id}>
                <button
                  onClick={() => {
                    setSelectedDate(log.date)
                    setDetailOpen(true)
                    setExpandedId(prev => prev === log.id ? null : log.id)
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    isSelected ? 'bg-[#06539B] text-white' : 'hover:bg-[#F2F0EB]'
                  }`}
                >
                  <WIcon size={14} style={{ color: isSelected ? 'white' : wCfg.color }} />
                  <span className="text-[10px] font-[700] flex-1">{formatDateShort(log.date)}</span>
                  <span className={`text-[9px] font-[600] ${isSelected ? 'text-white/70' : 'text-[#9E9890]'}`}>
                    {log.crewCount} {isEs ? 'pers' : 'crew'} · {log.hoursWorked}h
                  </span>
                  {log.issues?.length > 0 && <AlertTriangle size={11} className={isSelected ? 'text-white/70' : 'text-[#EF4444]'} />}
                </button>
                {isExpanded && (
                  <div className="mt-1 mb-2 ml-6 pl-3 border-l-2 border-[#ECEAE4] space-y-2">
                    {(log.workPerformed || log.workPerformedEs) && (
                      <div>
                        <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">
                          {isEs ? 'Trabajo realizado' : 'Work Performed'}
                        </div>
                        <p className="text-[11px] font-[600] text-[#1C1A17] leading-relaxed">
                          {isEs ? (log.workPerformedEs || log.workPerformed) : (log.workPerformed || log.workPerformedEs)}
                        </p>
                      </div>
                    )}
                    {(log.issues || log.issuesEs) && (
                      <div>
                        <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#EF4444] mb-1 flex items-center gap-1">
                          <AlertTriangle size={10} /> {isEs ? 'Problemas' : 'Issues'}
                        </div>
                        <p className="text-[11px] font-[600] text-[#1C1A17] leading-relaxed">
                          {isEs ? (log.issuesEs || log.issues) : (log.issues || log.issuesEs)}
                        </p>
                      </div>
                    )}
                    {(log.safetyNotes || log.safetyNotesEs) && (
                      <div>
                        <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#F59E0B] mb-1 flex items-center gap-1">
                          <HardHat size={10} /> {isEs ? 'Seguridad' : 'Safety'}
                        </div>
                        <p className="text-[11px] font-[600] text-[#1C1A17] leading-relaxed">
                          {isEs ? (log.safetyNotesEs || log.safetyNotes) : (log.safetyNotes || log.safetyNotesEs)}
                        </p>
                      </div>
                    )}
                    {(log.materialDeliveries || log.materialDeliveriesEs) && (
                      <div>
                        <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1 flex items-center gap-1">
                          <Truck size={10} /> {isEs ? 'Material' : 'Materials'}
                        </div>
                        <p className="text-[11px] font-[600] text-[#1C1A17] leading-relaxed">
                          {isEs ? (log.materialDeliveriesEs || log.materialDeliveries) : (log.materialDeliveries || log.materialDeliveriesEs)}
                        </p>
                      </div>
                    )}
                    {/* Clear return path: opening a log is never a trap. This
                        collapses the row AND closes the main detail panel, so
                        the copy ("return to list") matches the behavior — the
                        user lands back in neutral list-only browsing. */}
                    <button
                      onClick={() => { setExpandedId(null); setDetailOpen(false) }}
                      className="text-[10px] font-[800] text-[#06539B] hover:underline pt-1"
                    >
                      {isEs ? '← Cerrar y volver a la lista' : '← Close and return to list'}
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* ── EXPORT MODAL ─────────────────────────── */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[16px] font-[900] text-[#1C1A17]">{t('dailyLog.export.title')}</h2>
              <button onClick={() => setShowExportModal(false)} className="text-[#9E9890] hover:text-[#1C1A17]">
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-[10px] font-[800] tracking-[0.5px] text-[#9E9890] uppercase block mb-1">{t('dailyLog.export.from')}</label>
                <input type="date" value={exportFrom} onChange={e => setExportFrom(e.target.value)}
                       className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] focus:border-[#06539B] focus:outline-none" />
              </div>
              <div>
                <label className="text-[10px] font-[800] tracking-[0.5px] text-[#9E9890] uppercase block mb-1">{t('dailyLog.export.to')}</label>
                <input type="date" value={exportTo} onChange={e => setExportTo(e.target.value)}
                       className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] focus:border-[#06539B] focus:outline-none" />
              </div>
            </div>

            {preview && (
              <div className="bg-[#F2F0EB] rounded-lg p-3 mb-4 text-[11px] text-[#1C1A17]">
                <div>{t('dailyLog.export.summary', {
                  days: preview.days,
                  logs: preview.totalLogs,
                  photos: preview.totalPhotos,
                  sizeMb: (preview.estimatedSizeKb / 1024).toFixed(1),
                })}</div>

                {preview.tier === 1 && (
                  <div className="mt-2 text-green-700 font-[700]">{t('dailyLog.export.tier1Ready')}</div>
                )}

                {preview.tier === 2 && (
                  <div className="mt-3">
                    <div className="text-[10px] font-[800] uppercase text-[#9E9890] mb-2">{t('dailyLog.export.chooseMode')}</div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setExportMode('optimized')}
                        className={`flex-1 px-3 py-2 rounded-lg text-[11px] font-[700] ${exportMode === 'optimized' ? 'bg-[#06539B] text-white' : 'bg-white border border-[#ECEAE4]'}`}>
                        {t('dailyLog.export.optimized')}
                      </button>
                      <button type="button" onClick={() => setExportMode('full')}
                        className={`flex-1 px-3 py-2 rounded-lg text-[11px] font-[700] ${exportMode === 'full' ? 'bg-[#06539B] text-white' : 'bg-white border border-[#ECEAE4]'}`}>
                        {t('dailyLog.export.full')}
                      </button>
                    </div>
                  </div>
                )}

                {preview.tier === 3 && (
                  <div className="mt-3">
                    <div className="text-[10px] font-[800] uppercase text-[#9E9890] mb-2">{t('dailyLog.export.tier3Warning')}</div>
                    <div className="space-y-2">
                      {(['weekly', 'monthly', 'optimized'] as const).map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="split" value={opt}
                                 checked={opt === 'optimized' ? exportSplit === 'none' : exportSplit === opt}
                                 onChange={() => setExportSplit(opt === 'optimized' ? 'none' : opt)} />
                          <span className="text-[11px] font-[600]">
                            {t(`dailyLog.export.tier3.${opt}`)}
                            {opt === 'weekly' && (
                              <span className="ml-1 text-[9px] text-[#06539B] font-[800]">{t('dailyLog.export.recommended')}</span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={() => setShowExportModal(false)}
                      className="flex-1 border border-[#ECEAE4] rounded-full py-2 text-[11px] font-[800] text-[#9E9890]">
                {isEs ? 'Cancelar' : 'Cancel'}
              </button>
              <button onClick={handleExport} disabled={!preview || exporting}
                      className="flex-1 bg-[#06539B] text-white rounded-full py-2 text-[11px] font-[800] disabled:opacity-50 hover:bg-[#14457E]">
                {exporting ? t('dailyLog.export.generating') : t('dailyLog.export.download')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PHOTO SELECTOR MODAL (select existing Progress Photos) ─────────
          Shows ONLY photos that already exist in Progress Photos. No upload,
          no camera — Daily Log selects existing evidence. Mobile: large
          thumbnails with a sticky bottom "Add selected" action bar. */}
      {showPhotoSelector && photoLinkingEnabled && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
          onClick={() => setShowPhotoSelector(false)}
          role="dialog"
          aria-modal="true"
          aria-label={isEs ? PHOTO_LINK_COPY.selectFromProgress.es : PHOTO_LINK_COPY.selectFromProgress.en}
          data-testid="daily-log-photo-selector"
        >
          <div
            className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl overflow-hidden max-h-[92vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#ECEAE4]">
              <h3 className="text-[14px] font-[900] tracking-[-0.3px] text-[#1C1A17]">
                {isEs ? PHOTO_LINK_COPY.selectFromProgress.es : PHOTO_LINK_COPY.selectFromProgress.en}
              </h3>
              <button
                onClick={() => setShowPhotoSelector(false)}
                aria-label={isEs ? PHOTO_LINK_COPY.cancel.es : PHOTO_LINK_COPY.cancel.en}
                className="p-2 rounded-full hover:bg-[#F2F0EB]"
              >
                <X size={18} className="text-[#1C1A17]" />
              </button>
            </div>

            {/* Filter tabs */}
            <div className="flex gap-2 px-4 py-2 border-b border-[#ECEAE4]">
              <button
                type="button"
                onClick={() => setSelectorFilter('same_day')}
                data-testid="daily-log-photo-filter-sameday"
                className={`text-[10px] font-[800] px-3 py-1.5 rounded-full border transition-colors ${selectorFilter === 'same_day' ? 'bg-[#06539B] text-white border-[#06539B]' : 'border-[#ECEAE4] text-[#9E9890]'}`}
              >
                {isEs ? PHOTO_LINK_COPY.sameDay.es : PHOTO_LINK_COPY.sameDay.en}
              </button>
              <button
                type="button"
                onClick={() => setSelectorFilter('all')}
                data-testid="daily-log-photo-filter-all"
                className={`text-[10px] font-[800] px-3 py-1.5 rounded-full border transition-colors ${selectorFilter === 'all' ? 'bg-[#06539B] text-white border-[#06539B]' : 'border-[#ECEAE4] text-[#9E9890]'}`}
              >
                {isEs ? PHOTO_LINK_COPY.allProject.es : PHOTO_LINK_COPY.allProject.en}
              </button>
            </div>

            {/* Photo grid (scrollable) */}
            <div className="overflow-y-auto p-4 flex-1">
              {candidatePhotos.length === 0 ? (
                <p className="text-[12px] font-[600] text-[#9E9890] italic text-center py-10">
                  {selectorFilter === 'same_day'
                    ? (isEs ? PHOTO_LINK_COPY.noSameDay.es : PHOTO_LINK_COPY.noSameDay.en)
                    : (isEs ? PHOTO_LINK_COPY.noProjectPhotos.es : PHOTO_LINK_COPY.noProjectPhotos.en)}
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {candidatePhotos.map(photo => {
                    const selected = selectedPhotoIds.has(photo.id)
                    const disabled = !!photo.alreadyLinked
                    return (
                      <button
                        type="button"
                        key={photo.id}
                        onClick={() => { if (!disabled) togglePhotoSelected(photo.id) }}
                        disabled={disabled}
                        data-testid="daily-log-photo-candidate"
                        aria-pressed={selected}
                        className={`relative rounded-xl overflow-hidden bg-[#F2F0EB] aspect-square text-left focus:outline-none focus:ring-2 focus:ring-[#06539B] ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <img
                          src={photoSrc(photo.url)}
                          alt={photo.description || (isEs ? 'Foto de avance' : 'Progress photo')}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        {/* Checkbox indicator */}
                        <span className={`absolute top-2 left-2 w-5 h-5 rounded-md border-2 flex items-center justify-center ${selected || disabled ? 'bg-[#06539B] border-[#06539B]' : 'bg-white/70 border-white'}`}>
                          {(selected || disabled) && <Check size={13} className="text-white" />}
                        </span>
                        {disabled && (
                          <span className="absolute bottom-1 left-1 right-1 text-center text-[8px] font-[800] text-white bg-black/50 rounded px-1 py-0.5">
                            {isEs ? 'Ya vinculada' : 'Already linked'}
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {photoLinkError && (
              <div className="text-[10px] font-[700] text-red-600 bg-red-50 border-t border-red-200 px-4 py-2">
                {photoLinkError}
              </div>
            )}

            {/* Sticky bottom action bar */}
            <div className="border-t border-[#ECEAE4] p-3 space-y-2 bg-white">
              <p className="text-[9px] font-[600] text-[#9E9890] text-center">
                {isEs ? PHOTO_LINK_COPY.saveOnlyNotice.es : PHOTO_LINK_COPY.saveOnlyNotice.en}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowPhotoSelector(false)}
                  className="flex-1 border border-[#ECEAE4] rounded-full py-2.5 text-[12px] font-[800] text-[#9E9890] hover:bg-[#F2F0EB]"
                >
                  {isEs ? PHOTO_LINK_COPY.cancel.es : PHOTO_LINK_COPY.cancel.en}
                </button>
                <button
                  type="button"
                  onClick={() => linkPhotosMutation.mutate(Array.from(selectedPhotoIds))}
                  disabled={selectedPhotoIds.size === 0 || linkPhotosMutation.isPending}
                  data-testid="daily-log-add-selected-btn"
                  className="flex-[2] bg-[#06539B] text-white rounded-full py-2.5 text-[12px] font-[800] hover:bg-[#14457E] disabled:opacity-50"
                >
                  {linkPhotosMutation.isPending
                    ? (isEs ? 'Agregando…' : 'Adding…')
                    : `${isEs ? PHOTO_LINK_COPY.addSelected.es : PHOTO_LINK_COPY.addSelected.en}${selectedPhotoIds.size > 0 ? ` (${selectedPhotoIds.size})` : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
